import React, {
  FunctionComponent,
  useState,
  useMemo,
} from 'react';
import { BrandColor } from '@styleseat/brand';
import { Box, Inline } from '@styleseat/ui';
import Dropdown from '../Dropdown';
import { Button, BodyText } from '../index';
import { ArrowsChevronBottomIcon } from '../Icons';

const DefaultItem = ({ children }) => (
  <Box paddingY={4} paddingX={20}>
    <BodyText>
      {children}
    </BodyText>
  </Box>
);

type ISelectProps<T = any> = {
  items: Array<T>;
  Item?: FunctionComponent;
  selectedItem: T | null;
  onChange: ({ selectedItem }: { selectedItem: T }) => void;
  appearance?: React.ComponentProps<typeof Button>['appearance'];
  placeholder?: string;
  hideDropdownIcon?: boolean;
  testID?: string;
  minWidth?: number;
  itemToString: (item: T) => string;
  onIsOpenChange?: (isOpenNow: boolean) => void;
  borderless?: boolean;
  disabled?: boolean;
};

/**
 * A Select component built atop of the primitive/agnostic Dropdown component.
 * It is a subset of the Dropdown possibilities, designed to for being a
 * being a select.
 *
 * @param items - an array of strings (could support {label, value} in future)
 * @param Item? - a wrapping component each item is render in, so that items
 *                can be styled however desired
 * @param selectedItem? - if provided, makes the Select a controlled component
 * @param handleChange - fires when the selected item changes
 * @param testID? - string passed to Dropdown and used as base for other testIDs
 *
 * Example
 * ```
 * const [value, setValue] = useState();
 * <Select
 *   items={['foo', 'bar']}
 *   Item={({children}) => <Box>{children}</Box>}
 *   selectedItem={value}
 *   handleChange={({selectedItem}) => setValue(selectedItem)}
 * />
 * ```
 * @param appearance
 * @param placeholder
 * @param hideDropdownIcon
 * @param minWidth
 * @param itemToString
 * @param borderless
 */
// eslint-disable-next-line @typescript-eslint/comma-dangle
const Select = <T, >({
  items,
  Item = DefaultItem,
  selectedItem,
  onChange,
  testID,
  appearance = 'select',
  placeholder = 'Choose One',
  hideDropdownIcon = false,
  minWidth,
  itemToString,
  onIsOpenChange,
  borderless = false,
  disabled,
}: ISelectProps<T>) => {
  const [isFocused, setIsFocused] = useState(false);
  const selectedItemString = useMemo<string>(
    () => itemToString(selectedItem),
    [
      itemToString,
      selectedItem,
    ],
  );

  const onFocus = focused => {
    setIsFocused(focused);
  };

  const borderWidth = () => {
    if (borderless) return 0;
    if (isFocused) return 2;
    return 1;
  };

  let buttonTextColor = selectedItem !== undefined ? BrandColor.BackInBlack : BrandColor.Silver;

  if (disabled && selectedItem !== undefined) {
    buttonTextColor = BrandColor.Metal;
  }

  return (
    <Dropdown<T>
      selectedItem={selectedItem}
      onChange={onChange}
      onIsOpenChange={onIsOpenChange}
      testID={testID}
      Item={Item}
      minWidth={minWidth}
      onFocus={onFocus}
      itemToString={itemToString}
      Trigger={({ ...props }) => (
        <Button
          testID={testID && `${testID}-button`}
          appearance={appearance}
          disabled={disabled}
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            backgroundColor: disabled ? BrandColor.LightGrey : BrandColor.WhiteWash,
            borderColor: isFocused ? BrandColor.BackInBlack : BrandColor.Silver,
            borderRadius: 4,
            borderWidth: borderWidth(),
            height: 'auto',
            paddingLeft: 0,
            color: buttonTextColor,
            cursor: disabled ? 'default' : undefined,
          }}
          {...props}
        >
          <Inline
            justifyContent={hideDropdownIcon ? 'flex-start' : 'space-between'}
            alignItems="center"
            padding={12}
          >
            <BodyText color={selectedItem ? BrandColor.Metal : BrandColor.Silver}>
              {selectedItemString || placeholder}
            </BodyText>
            {!hideDropdownIcon && (
              <ArrowsChevronBottomIcon color={BrandColor.Metal} width={12} height={12} />
            )}
          </Inline>
        </Button>
      )}
    >
      {items}
    </Dropdown>
  );
};

export default Select;
